// Aug-2026 — Phase 2A: "Vendor & Procurement — Purchase Requisitions" tab
// (opened from the Projects list). Register + detail + lifecycle actions.
// Jun-2026 — Phase 2B: Approved PR → EXISTING Draft PO conversion actions
// (convert / open existing PO screen / comparison / controlled draft cancel).
import { useCallback, useEffect, useState } from "react";
import { api, formatINR, formatDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFormTab } from "@/hooks/useFormTab";
import { toast } from "sonner";
import {
  Plus, X, RefreshCw, Eye, Send, Ban, FileSpreadsheet, Paperclip,
  ShieldAlert, History, Download, ArrowRightLeft, ExternalLink,
  GitCompareArrows, Camera, Clock, Search } from "lucide-react";
import { EvidencePhotos } from "@/components/EvidencePhotos";
import { compressImage } from "@/lib/imageCompress";
import ProjectPRForm from "@/components/ProjectPRForm";
import ProjectPoComparison from "@/components/ProjectPoComparison";
import PoPaymentsSection from "@/components/PoPaymentsSection";
import BoqVendorPoModal from "@/components/BoqVendorPoModal";
import { InvoiceModal } from "@/pages/InvoicePage";

const S_BADGE = {
  Draft: "bg-amber-100 text-amber-800 border-amber-300",
  "Pending Approval": "bg-sky-100 text-sky-800 border-sky-300",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300",
  Cancelled: "bg-slate-200 text-slate-600 border-slate-300",
};
const CLASSES = ["", "Inventory/Material/Consumable", "Asset", "Service/Non-stock"];
const STATUSES = ["", "Draft", "Pending Approval", "Approved", "Rejected", "Cancelled"];

const Badge = ({ s }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${S_BADGE[s] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{s}</span>
);

async function blobDownload(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  downloadFile(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function PRDetail({ project, pr, onClose, onChanged, canEdit }) {
  const pid = project.id;
  const { hasPerm } = useAuth();
  const commercial = !!pr.commercials_visible;
  const [imgLine, setImgLine] = useState(null);
  const attachOpen = canEdit && pr.status !== "Cancelled";

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/projects/${pid}/prs/${pr.id}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.duplicate) toast.info("Identical file already attached — no new slot consumed");
      else toast.success("Attachment uploaded");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const uploadLineDoc = async (line, e) => {
    let f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Images: any size — auto-compressed below 500 KB (DB optimisation).
    if (/^image\//.test(f.type) || /\.(jpe?g|png)$/i.test(f.name)) {
      try { f = await compressImage(f, 500 * 1024); } catch { /* server enforces cap */ }
    } else if (f.size > 500 * 1024) {
      return toast.error("Line PDF must be 500 KB or smaller");
    }
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/projects/${pid}/prs/${pr.id}/lines/${line.line_uid}/attachment`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Line attachment uploaded");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="border border-slate-300 rounded-lg bg-white p-4 space-y-3" data-testid="pr-detail">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900">
          {pr.pr_number} <Badge s={pr.status} />
          {pr.priority && <span className="ml-2 text-[10px] uppercase text-slate-500">Priority: {pr.priority}</span>}
        </div>
        <button data-testid="pr-detail-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
      </div>
      {pr.status === "Approved" && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-3 py-2" data-testid="pr-approved-note">
          {pr.approved_note || "Approved — PO conversion will become available after the Project PO integration phase is activated."}
        </div>
      )}
      {pr.status === "Rejected" && pr.rejection_remarks && (
        <div className="text-xs text-rose-800 bg-rose-50 border border-rose-300 rounded px-3 py-2" data-testid="pr-rejection-remarks">
          Rejected: {pr.rejection_remarks}
        </div>
      )}
      {pr.status === "Cancelled" && (
        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-300 rounded px-3 py-2">
          Cancelled by {pr.cancelled_by}: {pr.cancellation_reason}
        </div>
      )}
      <div className="grid md:grid-cols-4 gap-2 text-xs">
        <div><span className="text-slate-500">PR Date:</span> {formatDate(pr.pr_date)}</div>
        <div data-testid="pr-detail-vendor"><span className="text-slate-500">Vendor:</span> <b>{pr.vendor_code ? `${pr.vendor_code} · ` : ""}{pr.vendor_name}</b></div>
        <div><span className="text-slate-500">Required by:</span> {pr.required_by_date ? formatDate(pr.required_by_date) : "—"}</div>
        <div><span className="text-slate-500">Created by:</span> {pr.created_by}</div>
        <div className="md:col-span-2"><span className="text-slate-500">Delivery:</span> {pr.delivery_location || "—"}</div>
        <div className="md:col-span-2"><span className="text-slate-500">Purpose:</span> {pr.purpose || "—"}</div>
        {pr.remarks && <div className="md:col-span-4"><span className="text-slate-500">Remarks:</span> {pr.remarks}</div>}
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full min-w-[720px] text-xs" data-testid="pr-detail-lines">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Item</th>
              <th className="px-2 py-1.5 text-left">Routing</th>
              <th className="px-2 py-1.5 text-left">UOM</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              {commercial && <><th className="px-2 py-1.5 text-right">Est Rate</th><th className="px-2 py-1.5 text-right">Tax %</th><th className="px-2 py-1.5 text-right">Est Total</th></>}
              <th className="px-2 py-1.5 text-left">Req Date</th>
              <th className="px-2 py-1.5 text-left">BOQ Ref</th>
              <th className="px-2 py-1.5 text-left">Activity</th>
              <th className="px-2 py-1.5 text-left">Photos</th>
              <th className="px-2 py-1.5 text-left">Doc</th>
            </tr>
          </thead>
          <tbody>
            {(pr.line_items || []).map(l => (
              <tr key={l.line_no} className="border-t border-slate-100" data-testid={`pr-detail-line-${l.line_no}`}>
                <td className="px-2 py-1">{l.line_no}</td>
                <td className="px-2 py-1 max-w-[220px]"><span className="font-mono text-[10px]">{l.item_code}</span> {l.item_name}</td>
                <td className="px-2 py-1 text-[10px]">{l.routing_class}</td>
                <td className="px-2 py-1">{l.uom}</td>
                <td className="px-2 py-1 text-right tabular-nums">{l.quantity}</td>
                {commercial && <><td className="px-2 py-1 text-right tabular-nums">{l.estimated_rate ?? ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.tax_percent ?? ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.total_value != null ? formatINR(l.total_value) : ""}</td></>}
                <td className="px-2 py-1">{l.required_date || ""}</td>
                <td className="px-2 py-1 font-mono text-[10px]" title={l.boq_element_name}>{l.boq_element_code || ""}</td>
                <td className="px-2 py-1 max-w-[140px] truncate" title={l.work_activity}>{l.work_activity}</td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {l.line_uid ? (
                    <button data-testid={`pr-line-photos-${l.line_no}`}
                      onClick={() => setImgLine(imgLine?.line_uid === l.line_uid ? null : l)}
                      className="text-slate-700 hover:underline inline-flex items-center gap-1">
                      <Camera size={11} /> {(l.images || []).length}/3
                    </button>
                  ) : <span className="text-[9px] text-slate-400">save to enable</span>}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {l.attachment ? (
                    <button data-testid={`pr-line-doc-dl-${l.line_no}`}
                      onClick={() => blobDownload(`/projects/${pid}/prs/${pr.id}/lines/${l.line_uid}/attachment`, l.attachment.filename)}
                      className="text-sky-700 hover:underline inline-flex items-center gap-1"
                      title={`${l.attachment.filename} (${Math.round(l.attachment.size / 1024)} KB)`}>
                      <Paperclip size={11} /> Doc
                    </button>
                  ) : (attachOpen && hasPerm("project_pr", "edit") && l.line_uid ? (
                    <label className="text-slate-500 hover:text-slate-800 cursor-pointer inline-flex items-center gap-1" title="Attach document — max 50 KB (PDF or image)">
                      <Paperclip size={11} /> Add
                      <input data-testid={`pr-line-doc-up-${l.line_no}`} type="file" className="hidden" accept=".pdf,image/*"
                        onChange={(e) => uploadLineDoc(l, e)} />
                    </label>
                  ) : <span className="text-slate-300">—</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {imgLine && (
        <div className="border border-slate-200 rounded p-2 bg-slate-50/60" data-testid="pr-line-photos-panel">
          <EvidencePhotos title={`Line ${imgLine.line_no} images — ${imgLine.item_name}`} max={3}
            photos={imgLine.images || []}
            canUpload={attachOpen && hasPerm("project_pr", "edit")}
            uploadUrl={`/projects/${pid}/prs/${pr.id}/lines/${imgLine.line_uid}/images`}
            photoUrl={(id) => `/projects/${pid}/prs/${pr.id}/lines/${imgLine.line_uid}/images/${id}`}
            onChanged={onChanged} testidPrefix={`pr-line-img-${imgLine.line_no}`} />
        </div>
      )}
      {commercial && (
        <div className="text-xs text-slate-700 text-right" data-testid="pr-detail-totals">
          Estimated: <b className="tabular-nums">{formatINR(pr.est_subtotal || 0)}</b> + tax <b className="tabular-nums">{formatINR(pr.est_tax_total || 0)}</b> = <b className="tabular-nums">{formatINR(pr.est_total || 0)}</b>
          <span className="text-[9px] text-slate-400 ml-1">(pre-commercial estimate — never posted to GL)</span>
        </div>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <div className="text-xs" data-testid="pr-attachments">
          <div className="font-bold text-slate-700 mb-1 inline-flex items-center gap-1"><Paperclip size={12} /> Attachments ({(pr.attachments || []).length}/3)</div>
          {(pr.attachments || []).length === 0 && <div className="text-slate-400">None</div>}
          {(pr.attachments || []).map(a => (
            <div key={a.id} className="flex items-center gap-2 py-0.5 flex-wrap">
              <button data-testid={`pr-att-dl-${a.id}`} onClick={() => blobDownload(`/projects/${pid}/prs/${pr.id}/attachments/${a.id}`, a.filename)}
                className="text-sky-700 hover:underline inline-flex items-center gap-1"><Download size={11} /> {a.filename}</button>
              <span className="text-[9px] text-slate-400">{Math.round(a.size / 1024)} KB</span>
              {a.added_after_posting && (
                <span data-testid={`pr-att-late-${a.id}`}
                  className="inline-flex items-center gap-0.5 bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded">
                  <Clock size={9} /> Added after posting
                </span>
              )}
            </div>
          ))}
          {attachOpen && (pr.attachments || []).length < 3 && (
            <label className="mt-1 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold border border-slate-300 rounded cursor-pointer hover:bg-slate-50">
              <Plus size={10} /> Attach file
              <input data-testid="pr-attach-input" type="file" className="hidden" accept=".pdf,image/*" onChange={uploadFile} />
            </label>
          )}
          <div className="text-[9px] text-slate-400 mt-1">PDF ≤5 MB or image ≤500 KB · max 3 · append-only (files added after approval are labelled)</div>
        </div>
        <div className="text-xs flex-1" data-testid="pr-status-history">
          <div className="font-bold text-slate-700 mb-1 inline-flex items-center gap-1"><History size={12} /> Status History (append-only)</div>
          {(pr.status_history || []).map((h, i) => (
            <div key={i} className="text-[11px] text-slate-600 border-t border-slate-100 py-0.5">
              <span className="font-mono text-slate-400">{formatDate(h.at)}</span> · <b>{h.action}</b> {h.from_status && `(${h.from_status} → ${h.to_status})`} · {h.by}
              {h.comment && <span className="text-rose-700"> — {h.comment}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// UX-1 — read-only compact PR / PO / GRN / PI metrics strip (server-masked)
function VendorSummaryStrip({ pid }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/projects/${pid}/vendor-summary`).then(r => setS(r.data)).catch(() => setS(null));
  }, [pid]);
  if (!s) return null;
  const cell = (label, b, testid) => (
    <div className="flex-1 min-w-[130px] border border-slate-200 rounded-lg bg-white px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className="text-sm font-bold text-slate-900 tabular-nums">{b.count}
        <span className="text-[10px] font-normal text-slate-400 ml-1">docs</span></div>
      <div className="text-[11px] tabular-nums text-slate-600">
        {b.pre_gst_amount != null ? `${formatINR(b.pre_gst_amount)} pre-GST` : "value hidden"}
      </div>
    </div>
  );
  return (
    <div data-testid="vendor-summary-strip">
      <div className="flex flex-wrap gap-2">
        {cell("Purchase Requisitions", s.pr, "vendor-summary-pr")}
        {cell("Purchase Orders", s.po, "vendor-summary-po")}
        {cell("Goods Receipts", s.grn, "vendor-summary-grn")}
        {cell("Purchase Invoices", s.pi, "vendor-summary-pi")}
      </div>
      <div className="text-[9px] text-slate-400 mt-1">{s.note}</div>
    </div>
  );
}

export default function ProjectPRTab({ project, onClose }) {
  const pid = project.id;
  const { user, hasPerm } = useAuth();
  const openFormTab = useFormTab();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState("");
  const [mode, setMode] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [compareFor, setCompareFor] = useState(null); // pr id for comparison modal
  const [filters, setFilters] = useState({ q: "", status: "", vendor_id: "", created_by: "", classification: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [vendors, setVendors] = useState([]);
  // Jun-2026 — PO Payments: read-only payment trail (dedicated permission).
  const canPayments = hasPerm("po_payments", "view");
  const [section, setSection] = useState("prs");
  const [boqPoOpen, setBoqPoOpen] = useState(false);
  const [boqPoAvail, setBoqPoAvail] = useState(null);

  const load = useCallback(() => {
    const params = { page, limit: 25 };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    return api.get(`/projects/${pid}/prs`, { params })
      .then(r => { setData(r.data); setForbidden(""); })
      .catch(e => setForbidden(e?.response?.data?.detail || "Project PRs are not available"));
  }, [pid, page, filters]);
  useEffect(() => { load(); }, [load]);
  const [refreshing, setRefreshing] = useState(false);
  // Aug-2026 (user report) — header refresh is now section-aware: on the
  // "PO Payments" sub-tab it refreshes the payment trail + requests panel
  // (it previously only reloaded Purchase Requisitions).
  const [payRefresh, setPayRefresh] = useState(0);
  const refresh = async () => {
    setRefreshing(true);
    if (section === "payments") {
      setPayRefresh((k) => k + 1);
      setRefreshing(false);
      toast.success("Payment data refreshed");
      return;
    }
    await load();
    setRefreshing(false);
    toast.success("Purchase Requisitions refreshed");
  };
  useEffect(() => {
    api.get(`/projects/${pid}/boq-vendor-po/context`, { params: { probe: 1 } })
      .then((r) => setBoqPoAvail(r.data)).catch(() => setBoqPoAvail(null));
  }, [pid]);
  useEffect(() => {
    api.get("/vendors/picker")
      .then(r => setVendors(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const openDetail = async (pr) => {
    try {
      const r = await api.get(`/projects/${pid}/prs/${pr.id}`);
      setDetail(r.data); setMode("list"); setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };

  const submit = async (pr) => {
    if (!window.confirm(`Submit ${pr.pr_number} for approval?`)) return;
    setBusyId(pr.id);
    try {
      await api.post(`/projects/${pid}/prs/${pr.id}/submit`);
      toast.success("Submitted for approval");
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusyId(""); }
  };

  const cancel = async (pr) => {
    const reason = window.prompt(`Cancellation reason for ${pr.pr_number} (mandatory):`);
    if (reason === null) return;
    setBusyId(pr.id);
    try {
      await api.post(`/projects/${pid}/prs/${pr.id}/cancel`, { reason });
      toast.success("PR cancelled");
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusyId(""); }
  };

  // ── Phase 2B: Approved PR → existing Draft PO conversion ──
  const convertToPo = async (pr) => {
    if (!window.confirm(`Convert ${pr.pr_number} into a Draft Purchase Order in the existing PO module?`)) return;
    setBusyId(pr.id);
    try {
      const r = await api.post(`/projects/${pid}/prs/${pr.id}/convert-to-po`);
      toast.success(r.data.already_converted
        ? `Already converted — existing Draft PO ${r.data.po_number}`
        : `Draft PO ${r.data.po_number} created in the Purchase Order module`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    finally { setBusyId(""); }
  };

  const openPo = async (pr) => {
    try {
      const r = await api.get(`/purchase-orders/${pr.converted_po_id}`);
      const po = r.data;
      openFormTab({
        path: `/purchase-orders/edit/${po.id}`,
        label: `Draft PO · ${po.po_number}`,
        parent: "/purchase-orders",
        onSaved: load,
        render: ({ close, saved }) => (
          <InvoiceModal type="purchase_order" invoice={po} prefilled={null}
            onClose={close} onSaved={saved} onViewList={close} />
        ),
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the PO"); }
  };

  const cancelDraftPo = async (pr) => {
    const reason = window.prompt(`Reason for cancelling Draft PO ${pr.converted_po_number} (mandatory — kept in history):`);
    if (reason === null) return;
    setBusyId(pr.id);
    try {
      await api.post(`/projects/${pid}/prs/${pr.id}/cancel-draft-po`, { reason });
      toast.success("Draft PO conversion cancelled — the PR may be re-converted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusyId(""); }
  };

  if (forbidden) {
    return (
      <div className="p-6" data-testid="pr-forbidden">
        <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm">
          <ShieldAlert size={16} /> {forbidden}
        </div>
      </div>
    );
  }

  const commercial = !!data?.commercials_visible;
  const canMakerAct = (pr) => user?.role === "admin" || pr.created_by === user?.email;
  const inp = "border border-slate-300 rounded px-2 py-1 text-xs";
  const inpF = `${inp} w-full md:w-auto`;

  // Shared row actions — rendered in the desktop table cell AND the mobile card.
  const rowActions = (pr) => (
    <>
      <button data-testid={`pr-view-${pr.pr_number?.replace(/\//g, "-")}`} onClick={() => openDetail(pr)} className="text-[#0B1F3A] hover:underline mr-2 font-bold inline-flex items-center gap-0.5"><Eye size={11} /> View</button>
      {["Draft", "Rejected"].includes(pr.status) && canMakerAct(pr) && hasPerm("project_pr", "edit") && (
        <button data-testid={`pr-edit-${pr.pr_number?.replace(/\//g, "-")}`}
          onClick={async () => { const r = await api.get(`/projects/${pid}/prs/${pr.id}`); setDetail(null); setEditing(r.data); setMode("form"); }}
          className="text-slate-700 hover:underline mr-2 font-bold">Edit</button>
      )}
      {["Draft", "Rejected"].includes(pr.status) && canMakerAct(pr) && hasPerm("project_pr", "edit") && (
        <button data-testid={`pr-submit-${pr.pr_number?.replace(/\//g, "-")}`} disabled={busyId === pr.id} onClick={() => submit(pr)}
          className="text-emerald-700 hover:underline mr-2 font-bold inline-flex items-center gap-0.5"><Send size={11} /> Submit</button>
      )}
      {["Draft", "Rejected", "Approved"].includes(pr.status) &&
        (pr.status === "Approved"
          ? hasPerm("project_pr", "delete")
          : (canMakerAct(pr) && (hasPerm("project_pr", "edit") || hasPerm("project_pr", "create") || hasPerm("project_pr", "delete")))) && (
        <button data-testid={`pr-cancel-${pr.pr_number?.replace(/\//g, "-")}`} disabled={busyId === pr.id} onClick={() => cancel(pr)}
          className="text-rose-700 hover:underline inline-flex items-center gap-0.5"><Ban size={11} /> Cancel</button>
      )}
      {/* ── Phase 2B: conversion actions ── */}
      {pr.status === "Approved" && !pr.converted_po_id && data?.po_conversion_enabled &&
        hasPerm("project_po_conversion", "create") && (
        <button data-testid={`pr-convert-${pr.pr_number?.replace(/\//g, "-")}`} disabled={busyId === pr.id}
          onClick={() => convertToPo(pr)}
          className="ml-2 text-[#F97316] hover:underline font-bold inline-flex items-center gap-0.5">
          <ArrowRightLeft size={11} /> Convert to Draft PO
        </button>
      )}
      {pr.converted_po_id && (
        <span className="ml-2 inline-flex items-center gap-1.5 flex-wrap" data-testid={`pr-converted-${pr.pr_number?.replace(/\//g, "-")}`}>
          <span className="inline-block bg-sky-100 border border-sky-300 text-sky-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
            title="Project Procurement PO approval will become available after the Project GRN receiving control is activated.">
            PO {pr.converted_po_number || ""} · {pr.converted_po_status || "Draft"}
          </span>
          {pr.converted_po_status === "Pending" && (pr.converted_po_pending_with || []).length > 0 && (
            <span className="inline-block bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold"
              data-testid={`pr-po-pending-with-${pr.pr_number?.replace(/\//g, "-")}`}
              title="Approvers this PO is currently waiting on (from the Approval Matrix)">
              Pending with: {(pr.converted_po_pending_with || []).join(", ")}
            </span>
          )}
          <button data-testid={`pr-open-po-${pr.pr_number?.replace(/\//g, "-")}`} onClick={() => openPo(pr)}
            className="text-sky-700 hover:underline font-bold inline-flex items-center gap-0.5">
            <ExternalLink size={11} /> Open PO
          </button>
          {(hasPerm("project_po_conversion", "view") || hasPerm("project_po_conversion", "create")) && (
            <button data-testid={`pr-compare-${pr.pr_number?.replace(/\//g, "-")}`} onClick={() => setCompareFor(pr.id)}
              className="text-slate-700 hover:underline font-bold inline-flex items-center gap-0.5">
              <GitCompareArrows size={11} /> Compare
            </button>
          )}
          {pr.converted_po_status === "Draft" && hasPerm("project_po_conversion", "delete") && (
            <button data-testid={`pr-cancel-po-${pr.pr_number?.replace(/\//g, "-")}`} disabled={busyId === pr.id}
              onClick={() => cancelDraftPo(pr)}
              className="text-rose-700 hover:underline font-bold inline-flex items-center gap-0.5">
              <Ban size={11} /> Cancel Draft
            </button>
          )}
        </span>
      )}
    </>
  );

  return (
    <div className="p-4 space-y-4" data-testid="pr-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">Vendor &amp; Procurement</div>
          <div className="text-lg font-bold text-slate-900">Purchase Requisitions — {project.project_name}</div>
          <div className="text-xs text-slate-500">{project.project_code} · operational &amp; pre-commercial (no GL / stock / commitment impact)</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasPerm("project_pr", "export") && (
            <button data-testid="pr-export-btn"
              onClick={() => blobDownload(`/projects/${pid}/prs-export`, `PR_Register_${project.project_code || "project"}.xlsx`)}
              className="px-3 py-1.5 text-xs font-bold border border-slate-300 text-slate-700 bg-white rounded hover:bg-slate-50 inline-flex items-center gap-1.5">
              <FileSpreadsheet size={13} /> Export Register
            </button>
          )}
          {boqPoAvail?.can_create && boqPoAvail?.has_boq && (
            <button data-testid="boqpo-open-btn" onClick={() => setBoqPoOpen(true)}
              title={`Raise a Draft PO directly from Approved BOQ ${boqPoAvail?.boq?.boq_number || ""} (no PR) with a uniform client-rate deduction — service items, no GRN`}
              className="px-3 py-1.5 text-xs font-bold bg-violet-700 text-white rounded hover:bg-violet-800 inline-flex items-center gap-1.5">
              <Plus size={13} /> BOQ Vendor PO
            </button>
          )}
          {hasPerm("project_pr", "create") && (
            <button data-testid="pr-new-btn" onClick={() => { setEditing(null); setDetail(null); setMode("form"); }}
              className="px-3 py-1.5 text-xs font-bold bg-[#F97316] text-white rounded hover:bg-[#ea6a0a] inline-flex items-center gap-1.5">
              <Plus size={13} /> New PR
            </button>
          )}
          <button data-testid="pr-refresh" onClick={refresh} disabled={refreshing} title="Refresh list"
            className="px-2 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /></button>
          {onClose && <button data-testid="pr-close" onClick={onClose} className="px-2 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50"><X size={13} /></button>}
        </div>
      </div>

      {canPayments && (
        <div className="flex gap-1" data-testid="vendor-section-toggle">
          {[["prs", "Purchase Requisitions"], ["payments", "PO Payments"]].map(([k, label]) => (
            <button key={k} data-testid={`vendor-section-${k}`} onClick={() => setSection(k)}
              className={`px-3 py-1 text-[11px] font-bold rounded-full border ${section === k
                ? "bg-[#0B1F3A] text-white border-[#0B1F3A]"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {section === "payments" && canPayments && <PoPaymentsSection project={project} refreshKey={payRefresh} />}

      {boqPoOpen && (
        <BoqVendorPoModal project={project} onClose={() => setBoqPoOpen(false)} />
      )}

      {section === "prs" && <>
      <VendorSummaryStrip pid={pid} />

      {!commercial && data && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-1.5" data-testid="pr-no-commercials-note">
          Estimated values (rates, taxes, totals) are hidden — you do not hold the PR Commercials permission.
        </div>
      )}

      {mode === "form" && (
        <ProjectPRForm project={project} existing={editing} commercialsVisible={commercial}
          onSaved={() => { setMode("list"); setEditing(null); load(); }}
          onClose={() => { setMode("list"); setEditing(null); }} />
      )}

      {detail && (
        <PRDetail project={project} pr={detail} canEdit={hasPerm("project_pr", "edit")}
          onClose={() => setDetail(null)}
          onChanged={() => openDetail(detail)} />
      )}

      {compareFor && (
        <ProjectPoComparison projectId={pid} prId={compareFor} onClose={() => setCompareFor(null)} />
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 items-end" data-testid="pr-filters">
          <label className="text-[10px] uppercase text-slate-500 col-span-2 md:col-span-1">Search<br />
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="pr-search" className={`${inp} pl-6 w-full md:w-64`}
                placeholder="PR no / PO no / vendor / item / purpose / location / creator…"
                value={filters.q} onChange={e => { setPage(1); setFilters(f => ({ ...f, q: e.target.value })); }} />
            </div></label>
          <label className="text-[10px] uppercase text-slate-500">From<br />
            <input data-testid="pr-filter-from" type="date" className={inpF} value={filters.date_from} onChange={e => { setPage(1); setFilters(f => ({ ...f, date_from: e.target.value })); }} /></label>
          <label className="text-[10px] uppercase text-slate-500">To<br />
            <input data-testid="pr-filter-to" type="date" className={inpF} value={filters.date_to} onChange={e => { setPage(1); setFilters(f => ({ ...f, date_to: e.target.value })); }} /></label>
          <label className="text-[10px] uppercase text-slate-500">Vendor<br />
            <select data-testid="pr-filter-vendor" className={`${inp} w-full md:w-auto md:max-w-[220px]`} value={filters.vendor_id} onChange={e => { setPage(1); setFilters(f => ({ ...f, vendor_id: e.target.value })); }}>
              <option value="">All</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_code ? `${v.vendor_code} · ` : ""}{v.name}</option>)}
            </select></label>
          <label className="text-[10px] uppercase text-slate-500">Status<br />
            <select data-testid="pr-filter-status" className={inpF} value={filters.status} onChange={e => { setPage(1); setFilters(f => ({ ...f, status: e.target.value })); }}>
              {STATUSES.map(s => <option key={s} value={s}>{s || "All"}</option>)}
            </select></label>
          <label className="text-[10px] uppercase text-slate-500">Classification<br />
            <select data-testid="pr-filter-class" className={inpF} value={filters.classification} onChange={e => { setPage(1); setFilters(f => ({ ...f, classification: e.target.value })); }}>
              {CLASSES.map(c => <option key={c} value={c}>{c || "All"}</option>)}
            </select></label>
          <label className="text-[10px] uppercase text-slate-500">Creator<br />
            <input data-testid="pr-filter-creator" className={inpF} placeholder="email…" value={filters.created_by} onChange={e => { setPage(1); setFilters(f => ({ ...f, created_by: e.target.value })); }} /></label>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[880px] text-xs" data-testid="pr-register-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
                <th className="px-2 py-1.5">PR Number</th>
                <th className="px-2 py-1.5">Date</th>
                <th className="px-2 py-1.5">Vendor</th>
                <th className="px-2 py-1.5">Required By</th>
                <th className="px-2 py-1.5 text-right">Lines</th>
                {commercial && <th className="px-2 py-1.5 text-right">Est Value</th>}
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Appr. Level</th>
                <th className="px-2 py-1.5">Created By</th>
                <th className="px-2 py-1.5">Last Action</th>
                <th className="px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map(pr => (
                <tr key={pr.id} className="border-t border-slate-100" data-testid={`pr-row-${pr.pr_number?.replace(/\//g, "-")}`}>
                  <td className="px-2 py-1.5 font-mono text-[11px] font-bold">{pr.pr_number}</td>
                  <td className="px-2 py-1.5">{formatDate(pr.pr_date)}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={pr.vendor_name}>{pr.vendor_code ? `${pr.vendor_code} · ` : ""}{pr.vendor_name}</td>
                  <td className="px-2 py-1.5">{pr.required_by_date ? formatDate(pr.required_by_date) : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{(pr.line_items || []).length}</td>
                  {commercial && <td className="px-2 py-1.5 text-right tabular-nums">{pr.est_total != null ? formatINR(pr.est_total) : ""}</td>}
                  <td className="px-2 py-1.5"><Badge s={pr.status} /></td>
                  <td className="px-2 py-1.5 tabular-nums">{pr.approval_level || "—"}</td>
                  <td className="px-2 py-1.5 max-w-[140px] truncate">{pr.created_by}</td>
                  <td className="px-2 py-1.5 text-slate-500">{formatDate(pr.last_action_at)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{rowActions(pr)}</td>
                </tr>
              ))}
              {data && (data.rows || []).length === 0 && (
                <tr><td colSpan={commercial ? 11 : 10} className="px-2 py-4 text-center text-slate-400">No Purchase Requisitions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked PR cards */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="pr-register-cards">
          {(data?.rows || []).map(pr => (
            <div key={pr.id} className="py-3 space-y-1.5" data-testid={`pr-card-${pr.pr_number?.replace(/\//g, "-")}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] font-bold">{pr.pr_number}</span>
                <Badge s={pr.status} />
                {commercial && pr.est_total != null && (
                  <span className="ml-auto text-xs font-bold tabular-nums">{formatINR(pr.est_total)}</span>
                )}
              </div>
              <div className="text-xs text-slate-700 break-words">{pr.vendor_code ? `${pr.vendor_code} · ` : ""}{pr.vendor_name}</div>
              <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Date: {formatDate(pr.pr_date)}</span>
                <span>Required by: {pr.required_by_date ? formatDate(pr.required_by_date) : "—"}</span>
                <span>Lines: {(pr.line_items || []).length}</span>
                <span>Level: {pr.approval_level || "—"}</span>
              </div>
              <div className="text-[10px] text-slate-400 break-all">{pr.created_by} · {formatDate(pr.last_action_at)}</div>
              <div className="flex flex-wrap items-center gap-y-1.5 pt-0.5">{rowActions(pr)}</div>
            </div>
          ))}
          {data && (data.rows || []).length === 0 && (
            <div className="py-4 text-center text-slate-400 text-xs">No Purchase Requisitions yet.</div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span>{data?.total || 0} PRs · page {page} of {Math.max(1, Math.ceil((data?.total || 0) / 25))}</span>
          <button data-testid="pr-page-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-0.5 border rounded disabled:opacity-40">Prev</button>
          <button data-testid="pr-page-next" disabled={page >= Math.ceil((data?.total || 0) / 25)} onClick={() => setPage(p => p + 1)} className="px-2 py-0.5 border rounded disabled:opacity-40">Next</button>
        </div>
      </div>
      </>}
    </div>
  );
}

